import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Store, MessageCircle, MapPin, Star, Package, Truck } from "lucide-react";
import API, { getImageUrl } from "../api";
import ProductCard from "../components/ProductCard";
import { StarRating } from "../components/ProductCard";
import { formatWhatsApp } from "../utils/whatsapp";

const formatPrice = (p) =>
  new Intl.NumberFormat("fr-SN", { style: "currency", currency: "XOF", minimumFractionDigits: 0 }).format(p);

export default function VendorShopPage() {
  const { id } = useParams();
  const [vendor, setVendor] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      API.get(`/api/vendors/${id}`),
      API.get(`/api/products?vendor_id=${id}&limit=50`),
    ]).then(([v, p]) => {
      setVendor(v.data);
      setProducts(p.data.products || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
      <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#FF9900] border-t-transparent" />
    </div>
  );

  if (!vendor) return (
    <div className="min-h-screen flex items-center justify-center">
      <p className="text-gray-500">Boutique introuvable</p>
    </div>
  );

  const waLink = vendor.whatsapp_number
    ? `https://wa.me/${formatWhatsApp(vendor.whatsapp_number)}?text=${encodeURIComponent(`Bonjour ${vendor.shop_name}, je vous contacte depuis Jokkoleen !`)}`
    : null;

  return (
    <div className="bg-[#F9FAFB] min-h-screen pb-24 md:pb-8">
      {/* Shop header */}
      <div className="bg-[#232F3E] text-white">
        <div className="w-full max-w-5xl mx-auto px-4 py-6">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-full bg-[#FF9900] flex items-center justify-center shrink-0 overflow-hidden">
              {vendor.avatar ? (
                <img src={getImageUrl(vendor.avatar)} alt={vendor.shop_name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
              ) : (
                <Store size={28} className="text-[#111827]" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-black text-white" data-testid="shop-name">{vendor.shop_name}</h1>
              {vendor.total_reviews > 0 && (
                <div className="flex items-center gap-2 mt-1">
                  <StarRating rating={vendor.rating} size={12} />
                  <span className="text-sm text-gray-300">{vendor.rating}/5 ({vendor.total_reviews} avis)</span>
                </div>
              )}
              {vendor.description && <p className="text-sm text-gray-300 mt-2 line-clamp-2">{vendor.description}</p>}
            </div>
          </div>

          {/* Payment & Contact */}
          <div className="flex flex-wrap gap-3 mt-4">
            {vendor.phone_wave && (
              <div className="flex items-center gap-1.5 bg-white/10 rounded-md px-3 py-1.5 text-xs">
                <div className="w-4 h-4 rounded-full bg-[#1CBBEE] flex items-center justify-center">
                  <span className="text-white font-black text-[8px]">W</span>
                </div>
                <span>{vendor.phone_wave}</span>
              </div>
            )}
            {vendor.phone_orange && (
              <div className="flex items-center gap-1.5 bg-white/10 rounded-md px-3 py-1.5 text-xs">
                <div className="w-4 h-4 rounded-full bg-[#FF6600] flex items-center justify-center">
                  <span className="text-white font-black text-[8px]">O</span>
                </div>
                <span>{vendor.phone_orange}</span>
              </div>
            )}
            {waLink && (
              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 bg-[#25D366] text-white font-bold px-3 py-1.5 rounded-md text-xs hover:bg-[#20BD5A] transition-colors"
                data-testid="vendor-whatsapp-contact"
              >
                <MessageCircle size={12} /> Contacter sur WhatsApp
              </a>
            )}
          </div>

          {/* Shipping info */}
          {(vendor.shipping_zones?.length > 0 || vendor.shipping_delay) && (
            <div className="mt-3 flex items-center gap-2 text-xs text-gray-300">
              <Truck size={12} />
              {vendor.shipping_zones?.length > 0 && <span>Livraison: {vendor.shipping_zones.join(", ")}</span>}
              {vendor.shipping_delay && <span>· Délai: {vendor.shipping_delay}</span>}
            </div>
          )}
        </div>
      </div>

      {/* Products */}
      <div className="w-full max-w-5xl mx-auto px-4 py-5">
        <h2 className="text-lg font-extrabold text-[#232F3E] mb-4">
          Produits ({products.length})
        </h2>
        {products.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-md border border-gray-200">
            <Package size={40} className="text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">Cette boutique n'a pas encore de produits</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {products.map((p) => (
              <ProductCard key={p._id} product={{ ...p, vendor_shop_name: vendor.shop_name }} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
